import type { ReactNode } from 'react';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { ProviderName } from '@atlaskit/editor-common/provider-factory';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { EmojiResource } from '@atlaskit/emoji';
import type { EmojiLoadSuccessCallback, EmojiLoadFailCallback } from '@atlaskit/emoji/resource';
import type { RendererAppearance, RendererProps, ExtensionViewportSize } from '@atlaskit/renderer';
import { RendererWithAnalytics as AkRenderer } from '@atlaskit/renderer';
import type { ObjectKey, TaskState } from '@atlaskit/task-decision';
import { fg } from '@atlaskit/platform-feature-flags';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';
import {
	getConfluencePageAri,
	useMemoizedBlockServiceAPIProviders,
	useMemoizedContentAPIProviders,
	useMemoizedSyncedBlockProvider,
} from '@atlaskit/editor-synced-block-provider';
import { getSyncedBlockNodeComponent } from '@atlaskit/editor-synced-block-renderer';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { getRendererSmartButtonExtensionHandlers } from '@confluence/automation-extensions/entry-points/getRendererSmartButtonExtensionHandlers';
import { PageLoadMark } from '@confluence/browser-metrics/entry-points/PageLoadMark';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext';
import { getRendererCustomSitesExtensionHandlers } from '@confluence/custom-sites-extensions/entry-points/getRendererCustomSitesExtensionHandlers';
import { useEditorFeatureFlags } from '@confluence/editor-features/entry-points/useEditorFeatureFlags';
import { useProfilecardProvider } from '@confluence/editor-features/entry-points/useProfilecardProvider';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext';
import { RendererAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/RendererAnalyticsListener';
import type { CreateEmojiProviderParametersType } from '@confluence/fabric-emoji-support';
import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider';
import type { ProfileCardProvider } from '@confluence/fabric-media-support/entry-points/types';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers';
import {
	ConfluenceTaskDecisionProvider,
	TaskBatcher,
} from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider';
import { createMentionsConfig } from '@confluence/fabric-media-support/entry-points/MentionsProvider';
import {
	createRendererMediaProvider,
	controlDataExportOnCertainFeatures,
} from '@confluence/fabric-media-support/entry-points/MediaProvider';
import { getSyncedBlockNestedRendererProviderCreator } from '@confluence/fabric-providers/entry-points/SyncedBlockProvider';
import {
	getEmojiConfig,
	getEmojiProvider,
} from '@confluence/fabric-providers/entry-points/EmojiProvider';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile';
import { getLogger } from '@confluence/logger';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { FeedFollowErrorDisplay } from '@confluence/profile/entry-points/FeedFollowErrorDisplay';
import { useFeedFollow } from '@confluence/profile/entry-points/useFeedFollow';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy';
import type { FeatureFlagsType } from '@confluence/session-data/entry-points/SessionDataTypes';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expVal } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import { useIsEligibleCreateNewWhiteboard } from '@confluence/prompt-competitor-link/entry-points/useIsEligibleCreateNewWhiteboard';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup';

import { VirtualizedTableComponent } from './VirtualizedTableComponent';
import {
	registerGlobalMediaViewedListener,
	removeGlobalMediaViewedListener,
} from './AttachmentEvents';
import { batchTaskUpdate } from './batchTaskUpdate';

export const ANALYTICS_EVENT_SEVERITY_TRACKING = {
	enabled: true,
	severityDegradedThreshold: 4747,
	severityNormalThreshold: 3775,
};

const logger = getLogger('ADFRenderer');
export const UNSUPPORTED_CONTENT_THRESHOLDS = {
	enabled: true,
	thresholds: {
		blocking: 1.5,
		degraded: 1,
	},
};

export interface ADFRendererProps extends RendererProps {
	getExtensionHandlers?: (
		setLegacyRendererFlag?: ((extensionKey?: string) => void) | undefined,
	) => ExtensionHandlers;
	getDataProviders?: () => ProviderFactory;
	onRendered?: () => void;
	contentId?: string;
	contentType?: string;
	appearance?: RendererAppearance;
	portal?: HTMLDivElement;
	renderWhenReady?: () => ReactNode;
	allowStickyHeaders?: boolean;
	queryHash?: string;
	isTruncatedContent?: boolean;
	maxHeight?: number;
	fadeOutHeight?: number;
	mediaToken?: string;
	isContentHeaderFixedAtTop?: boolean;
	extensionViewportSizes?: ExtensionViewportSize[];
	onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	onEmojiLoadFail?: EmojiLoadFailCallback;
	getExtensionHeight?: GetPMNodeHeight;
}

interface ADFRendererComponentProps extends ADFRendererProps {
	offsetTop: number;
	defaultScrollRootId_DO_NOT_USE?: string;
	shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE?: boolean;
	flags: FlagsStateContainer;
	allowRendererContainerStyles?: boolean;
}

type ProviderDefinitions = {
	[name: string]: Promise<any>;
};

type MergeDataProvidersOptions = {
	intl: IntlShape;
	cloudId: string;
	userId: string | null;
	userRole?: 'admin' | 'trusted' | 'basic';
	contentId: string;
	spaceKey: string;
	pageContextContentId?: string;
	flags: FlagsStateContainer;
	featureFlags: FeatureFlagsType;

	providerFactory?: ProviderFactory;
	getDataProviders?: () => ProviderFactory;
	profilecardProvider: Promise<ProfileCardProvider>;

	isExternalShare: boolean;
	hasViewUserProfilePermission: boolean;

	handleActionToggle: (objectKey: ObjectKey, state: TaskState) => void;
	handleInviteItemClick?: () => void;
	mediaToken?: string;
	emojiResourceParams?: CreateEmojiProviderParametersType;
};

const mergeDataProviders: (options: MergeDataProvidersOptions) => {
	providerFactory: ProviderFactory | undefined;
	providers: ProviderDefinitions;
} = ({
	hasViewUserProfilePermission,
	handleActionToggle,
	handleInviteItemClick,
	getDataProviders,
	providerFactory,
	isExternalShare,
	contentId,
	spaceKey,
	pageContextContentId,
	cloudId,
	userId,
	userRole,
	flags,
	profilecardProvider,
	intl,
	mediaToken,
	emojiResourceParams,
}) => {
	const providers: ProviderDefinitions = {};

	if (getDataProviders) {
		Object.assign(providers, getDataProviders());
	}

	if (contentId || mediaToken) {
		providers.mediaProvider = createRendererMediaProvider({
			contentId,
			spaceKey,
			mediaDspContentId: pageContextContentId,
			isExternalShare,
			mediaToken,
		});

		providers.taskDecisionProvider = Promise.resolve(
			new ConfluenceTaskDecisionProvider(intl, flags, contentId, handleActionToggle),
		);
	}

	if (cloudId && userId) {
		providers.mentionProvider = createMentionsConfig(
			cloudId,
			userId,
			undefined,
			undefined,
			handleInviteItemClick,
			userRole,
		)
			.then((config) => config.mentionResource)
			.catch((err) => {
				if ((process as any).env.NODE_ENV !== 'production') {
					logger.error`Failed to initialise mention resource ${err}`;
				}
				return Promise.reject();
			});
	}

	if (emojiResourceParams && cloudId) {
		providers.emojiProvider = getEmojiProvider(EmojiResource, {
			...emojiResourceParams,
			allowAnonymousAccess: true,
		});
	}

	if (cloudId) {
		// If global view user profile is enabled then user can view profile card
		if (hasViewUserProfilePermission) {
			providers.profilecardProvider = profilecardProvider;
		}
	}

	if (contentId && spaceKey) {
		providers.contextIdentifierProvider = Promise.resolve({
			containerId: spaceKey,
			objectId: contentId,
			product: 'confluence',
		});
	}

	providers.cardProvider = Promise.resolve(
		new EditorCardProvider(undefined, undefined, 'CONFLUENCE'),
	);

	const keys = Object.keys(providers);

	if (keys.length) {
		providerFactory = providerFactory || ProviderFactory.create({});
		for (const provider of Object.keys(providers)) {
			providerFactory.setProvider(provider as ProviderName, providers[provider]);
		}
	}

	return {
		providerFactory,
		providers,
	};
};

const projectLinkerHandlers = {
	'com.atlassian.confluence.project-linker': () => <></>,
};

export const unsupportedContentLevelsTrackingOptions =
	(): RendererProps['unsupportedContentLevelsTracking'] =>
		fg('platform_renderer_unsupported_content_tracking')
			? UNSUPPORTED_CONTENT_THRESHOLDS
			: undefined;

const DEFAULT_EXTENSION_HANDLERS = {};
const DEFAULT_GET_EXTENSION_HANDLERS = () => DEFAULT_EXTENSION_HANDLERS;
const DEFAULT_EXTENSION_VIEWPORT_SIZES = [];

export const ADFRendererComponent = memo((props: ADFRendererComponentProps) => {
	const {
		document,
		dataProviders,
		extensionHandlers = DEFAULT_EXTENSION_HANDLERS,
		getExtensionHandlers = DEFAULT_GET_EXTENSION_HANDLERS,
		eventHandlers,
		appearance,
		contentMode,
		adfStage = 'stage0',
		portal,
		onRendered,
		renderWhenReady,
		allowStickyHeaders = true,
		allowPlaceholderText = false,
		isTruncatedContent,
		maxHeight,
		fadeOutHeight,
		flags,
		offsetTop,
		defaultScrollRootId_DO_NOT_USE,
		shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
		mediaToken,
		onError,
		UNSTABLE_allowTableAlignment = true,
		UNSTABLE_allowTableResizing = true,
		UNSTABLE_isPresentational = false,
		extensionViewportSizes = DEFAULT_EXTENSION_VIEWPORT_SIZES,
		onEmojiLoadSuccess,
		onEmojiLoadFail,
		allowRendererContainerStyles,
		...rest
	} = props;
	const intl = useIntl();
	const [isTaskToggled, setIsTaskToggled] = useState<boolean>(false);

	const initialState = window['__SSR_SMARTLINKS__'];
	const isPageSSR = isPageSSRd();
	const storeOptions = useStoreOptions(initialState, isPageSSR);

	// Fix for HOT-99954 - Issue where embedded pages SSR SmartLinks open in same tab
	const shouldSSRsmartLinks =
		isPageSSR && !isEmbeddedConfluence_DO_NOT_USE() && !isMobileApp_DO_NOT_USE();

	const { cloudId, userId, featureFlags, isLicensed, isLoggedIn, isAdminHubAIEnabled } =
		useSessionData();

	const [spaceKey] = usePageSpaceKey();
	const [pageContextContentId] = usePageContentId();
	const [pageContextContentType] = useContentType();

	const { openCoreInvites, userRole } = useContext(CoreInvitesContext);

	const [shouldRefetchContent, setShouldRefetchContent] = useState(false);

	const contentId = props.contentId || pageContextContentId;
	const contentType = props.contentType || pageContextContentType;

	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const taskBatcherRef = useRef(
		new TaskBatcher(expVal('confluence_frontend_task_batching_interval', 'value', 3000)),
	);
	const feedFollowData = useFeedFollow();

	// Handlers for batched task update
	const handleBatchTaskUpdate = useCallback(
		(batch: TaskAction[]) => {
			// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
			void batchTaskUpdate(contentId, batch).then(() => {
				setIsTaskToggled(false);
				setShouldRefetchContent(true);
			});
		},
		[contentId, setShouldRefetchContent],
	);

	const handleActionToggle = useCallback(
		(objectKey: ObjectKey, state: TaskState) => {
			setIsTaskToggled(true);
			taskBatcherRef.current.add({ objectKey, state });
		},
		[taskBatcherRef],
	);

	useEffect(() => {
		// there is a document check - we do not want to call registerGlobalMediaViewedListener
		// with no content
		if (document) {
			registerGlobalMediaViewedListener({ contentType });
		}

		onRendered && onRendered();

		taskBatcherRef.current.onFlush(handleBatchTaskUpdate);

		return () => {
			if (document) {
				removeGlobalMediaViewedListener({ contentType });
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, taskBatcherRef, handleBatchTaskUpdate]);

	useEffect(() => {
		setShouldRefetchContent(false);
		// WS-4098 - When the contentId changes or when we mount we don't need to refetch
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [contentId]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isTaskToggled) {
				event.preventDefault();
				event.returnValue = ''; // This triggers the alert in all browsers
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTaskToggled, contentId]);

	// Setting up emoji resources for ssr and data providers
	const emojiResourceParams = useMemo(() => {
		if (cloudId) {
			return createEmojiProviderParameters({
				cloudId,
				userId,
				isLicensed,
				isLoggedIn,
				onEmojiLoadSuccess,
				onEmojiLoadFail,
			});
		}
	}, [cloudId, userId, isLicensed, isLoggedIn, onEmojiLoadSuccess, onEmojiLoadFail]);

	const emojiResourceConfig = useMemo(() => {
		if (emojiResourceParams) {
			return getEmojiConfig(
				emojiResourceParams.cloudId,
				emojiResourceParams.userId,
				emojiResourceParams.disableUpload,
				emojiResourceParams.emojiSSRConfig,
				undefined,
				emojiResourceParams.emojiLoadCallbacks,
			);
		}
	}, [emojiResourceParams]);

	const { isExternalShareRequest } = useContext(ExternalShareContext);

	const { push } = useRouteActions();

	const mergedExtensionHandlers = useMemo(() => {
		return {
			...getExtensionHandlers?.(),
			...extensionHandlers,
			...getRendererCustomSitesExtensionHandlers({ contentId }),
			...getRendererSmartButtonExtensionHandlers({
				contentId,
				contentType,
				cloudId,
			}),
			...projectLinkerHandlers,
		};
	}, [extensionHandlers, getExtensionHandlers, contentId, contentType, cloudId]);

	const mediaSSR = useMediaSSRModeAndAuthConfig({
		contentId,
		spaceKey,
		isExternalShare: isExternalShareRequest,
		mediaToken,
	});

	// -- Matches fabric-feature-flags.ts

	const isStickyHeadersEnabled = allowStickyHeaders;

	const errorHandler = useCallback(
		(error: Error) => {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.BACKBONE,
				});
			}
			onError?.(error);
		},
		[onError],
	);

	const { shimAdfUrls } = useNavigationPolicy();

	const editorFeatureFlags = useEditorFeatureFlags();

	const profilecardProvider = useProfilecardProvider({
		push,
		userId,
		cloudId,
		featureFlags,
	});

	const shouldControlDataExport = controlDataExportOnCertainFeatures(window.location.pathname);

	// ----------------------------------

	const allowHeadingAnchorLinks = useMemo(
		() => ({
			allowNestedHeaderLinks: true,
			activeHeadingId: props.queryHash,
		}),
		[props.queryHash],
	);

	const media = useMemo(
		() => ({
			allowLinking: true,
			allowCaptions: true,
			featureFlags: { mediaInline: true },
			ssr: mediaSSR,
		}),
		[mediaSSR],
	);

	const { isEligibleCreateNewWhiteboard } = useIsEligibleCreateNewWhiteboard();

	const smartLinks = useMemo(
		() => ({
			ssr: shouldSSRsmartLinks,
			hideHoverPreview: false,
			CompetitorPrompt: isEligibleCreateNewWhiteboard ? CompetitorPrompt : undefined,
		}),
		[shouldSSRsmartLinks, isEligibleCreateNewWhiteboard],
	);

	const stickyHeaders = useMemo(
		() =>
			isStickyHeadersEnabled
				? {
						offsetTop,
						defaultScrollRootId_DO_NOT_USE,
						shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
					}
				: undefined,
		[
			isStickyHeadersEnabled,
			offsetTop,
			defaultScrollRootId_DO_NOT_USE,
			shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
		],
	);

	const mergedDataProviders = mergeDataProviders({
		// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
		contentId,
		// @ts-ignore FIXME: `spaceKey` can be `undefined` here, and needs proper handling
		spaceKey,
		pageContextContentId,
		cloudId,
		userId,
		flags,
		featureFlags,
		push,
		intl,
		providerFactory: dataProviders,
		profilecardProvider,
		isExternalShare: isExternalShareRequest,
		handleActionToggle,
		hasViewUserProfilePermission,
		handleInviteItemClick: openCoreInvites,
		userRole,
		mediaToken,
		emojiResourceParams,
	});

	const { fetchProvider, writeProvider } = useMemoizedContentAPIProviders(cloudId);
	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders();
	const syncedBlockProvider = useMemoizedSyncedBlockProvider(
		fg('platform_synced_blocks_block_service_provider') ? blockServiceFetchProvider : fetchProvider,
		fg('platform_synced_blocks_block_service_provider') ? blockServiceWriteProvider : writeProvider,
		// we don't actually use the page ari for renderer reference sync blocks, so it is okay if contentId is undefined
		getConfluencePageAri(contentId ?? '', cloudId, contentType === 'page' ? 'page' : 'blogpost'),
		{
			parentDataProviders: {
				...mergedDataProviders.providers,
			},
			providerCreator: getSyncedBlockNestedRendererProviderCreator(cloudId, userId),
		},
	);

	const nodeComponents = useMemo(() => {
		const virtualizedTableComponent = expVal(
			'confluence_frontend_virtualized_tables',
			'isEnabled',
			false,
		)
			? {
					table: VirtualizedTableComponent,
				}
			: undefined;

		if (expVal('platform_synced_block', 'isEnabled', false)) {
			return {
				syncBlock: getSyncedBlockNodeComponent({
					syncBlockProvider: syncedBlockProvider,
					doc: document,
					syncBlockRendererOptions: undefined,
				}),
				...(virtualizedTableComponent || {}),
			};
		} else {
			return virtualizedTableComponent;
		}
	}, [syncedBlockProvider, document]);

	if (!document) {
		return null;
	}

	const finalDocument = shimAdfUrls(document) as DocNode;

	const content = (
		<>
			{(appearance === 'full-page' || appearance === 'full-width') && (
				<PageLoadMark data-testid="pageLoadMarkTestId" name="onBeforeAkRenderer" />
			)}
			<RendererAnalyticsListener
				// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
				objectId={contentId}
				objectType={contentType}
			>
				<AkRenderer
					// This flag is used for fixing table width in SSR mode and for hydration
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					enableSsrInlineScripts={Boolean(process.env.REACT_SSR)}
					// include renderer inline script from above on first render to prevent hydration mismatch
					noOpSSRInlineScript={renderServerPlaceholder}
					adfStage={adfStage}
					document={finalDocument}
					appearance={appearance}
					contentMode={contentMode}
					stickyHeaders={stickyHeaders}
					extensionHandlers={mergedExtensionHandlers}
					eventHandlers={eventHandlers}
					featureFlags={editorFeatureFlags}
					dataProviders={mergedDataProviders.providerFactory}
					portal={portal}
					allowColumnSorting
					allowHeadingAnchorLinks={allowHeadingAnchorLinks}
					allowAltTextOnImages
					media={media}
					emojiResourceConfig={emojiResourceConfig}
					allowCopyToClipboard
					allowWrapCodeBlock
					{...rest}
					useSpecBasedValidator
					truncated={isTruncatedContent}
					maxHeight={maxHeight}
					fadeOutHeight={fadeOutHeight}
					analyticsEventSeverityTracking={ANALYTICS_EVENT_SEVERITY_TRACKING}
					unsupportedContentLevelsTracking={unsupportedContentLevelsTrackingOptions()}
					allowPlaceholderText={allowPlaceholderText}
					allowCustomPanels
					smartLinks={smartLinks}
					extensionViewportSizes={extensionViewportSizes}
					onError={errorHandler}
					UNSTABLE_allowTableAlignment={UNSTABLE_allowTableAlignment}
					UNSTABLE_allowTableResizing={UNSTABLE_allowTableResizing}
					UNSTABLE_isPresentational={UNSTABLE_isPresentational}
					allowRendererContainerStyles={allowRendererContainerStyles}
					{...(nodeComponents && { nodeComponents })}
				/>
				{renderWhenReady && renderWhenReady()}
			</RendererAnalyticsListener>
			<ContentRefetchHandler contentId={contentId} shouldRefetch={shouldRefetchContent} />
			<FeedFollowErrorDisplay
				feedConfigError={feedFollowData.feedConfigError}
				mutationError={feedFollowData.mutationError}
				showErrorFlag
			/>
		</>
	);

	return (
		<>
			<ConfluenceSmartCardProvider
				{...getSmartCardRenderers()}
				{...storeOptions}
				isAdminHubAIEnabled={isAdminHubAIEnabled}
				shouldControlDataExport={shouldControlDataExport}
				product="CONFLUENCE"
			>
				{content}
			</ConfluenceSmartCardProvider>
		</>
	);
});
